/**
 * A segunda linha da busca de animais: raça e RGD.
 *
 * O nome sozinho não identifica. O rebanho tem muita vaca e muito touro com o
 * mesmo nome, e a lista de busca mostrava só isso — escolher era adivinhar.
 * O RGD é o que separa dois homônimos; a raça é o que se confere de olho, sem
 * consultar papel.
 *
 * Quando o RGD não está cadastrado a linha diz "sem RGD", em vez de omitir.
 * A omissão parece defeito da tela; dito, vira informação: aquele animal está
 * com o cadastro incompleto, e é por isso que não dá para distingui-lo do
 * outro de mesmo nome.
 *
 * A CATEGORIA fica de fora de propósito, embora a busca a devolva. Numa lista
 * de touros todo mundo é "Touro", e o que vale para toda linha não carrega
 * informação — é o mesmo critério das marcas de estado.
 */
export const descricaoDoAnimal = (animal = {}) => {
  const raca = (animal.breed || '').trim();
  const rgd = (animal.rgd || '').trim();
  return [raca, rgd ? `RGD ${rgd}` : 'sem RGD'].filter(Boolean).join(' · ');
};

/**
 * Quem é touro, quem é receptora, quem é doadora — para filtrar a busca.
 *
 * A busca do banco casa por brinco ou RGD e devolve todo tipo de animal junto:
 * quem escolhe o touro não quer ver a vaca de mesmo nome. Cada tela escrevia o
 * seu predicado à mão, e "o que é um touro" existia em duas cópias e "o que é
 * uma receptora" em outras duas. Categoria nova entra aqui e vale nas quatro.
 *
 * `Ambos` é a vaca que serve de doadora e de receptora, e por isso aparece nas
 * duas listas. `sex === 'M'` fica no touro como rede para cadastro antigo, que
 * nem sempre trazia categoria.
 */
export const ehTouro = (animal = {}) =>
  animal.category === 'Reprodutor' || animal.category === 'Touro' || animal.sex === 'M';

export const ehReceptora = (animal = {}) =>
  animal.category === 'Receptora' || animal.category === 'Ambos';

export const ehDoadora = (animal = {}) =>
  animal.category === 'Doadora' || animal.category === 'Ambos';

/** Nome comparável: o touro chega ao embrião digitado, com caixa e espaço soltos. */
export const chaveDoNome = (nome) => (nome || '').trim().toLowerCase();

/**
 * Liga ao cadastro as doadoras que só têm nome na linha.
 *
 * Doadora sem cadastro não pode: o nome solto não chega ao embrião, e na TE
 * ele aparecia sem doadora. Caso real: #2026-0129, a 1944 digitada na OPU sem
 * ser escolhida na lista — ela ESTAVA cadastrada, só não foi ligada.
 *
 * Por isso, antes de barrar, tenta ligar pelo nome: identificação ou RGD igual
 * (sem caixa nem espaço) a UMA doadora do laboratório. Havendo duas, desempata
 * a do cliente do serviço; sem desempate, não chuta.
 *
 * `campos` diz onde a linha guarda id e nome (`animal_id`/`animal_identifier`
 * na OPU, `donor_id`/`donor_identifier` na FIV). `ligar` monta a linha ligada.
 * Devolve as linhas e os nomes que ficaram sem cadastro.
 */
export const ligarDoadorasAoCadastro = (linhas, animais, { id, nome, clientId, ligar }) => {
  const doadoras = (animais || []).filter(ehDoadora);
  const semCadastro = [];
  const ligadas = (linhas || []).map(linha => {
    const chave = chaveDoNome(linha[nome]);
    if (linha[id] || !chave) return linha;
    const iguais = doadoras.filter(a =>
      chaveDoNome(a.identifier) === chave || (a.rgd && chaveDoNome(a.rgd) === chave));
    const doCliente = iguais.filter(a => clientId && a.client_id === clientId);
    const animal = iguais.length === 1 ? iguais[0] : doCliente.length === 1 ? doCliente[0] : null;
    if (animal) return ligar(linha, animal);
    semCadastro.push(linha[nome].trim());
    return linha;
  });
  return { linhas: ligadas, semCadastro: [...new Set(semCadastro)] };
};

/** A mensagem que barra o salvamento, igual na OPU e na FIV. */
export const avisoDeDoadoraSemCadastro = (nomes) =>
  `doadora sem cadastro: ${nomes.join(', ')}. Escolha na lista ou use "+ Cadastrar".`;

// Valor entre aspas no filtro `or` do PostgREST: nome de touro tem espaço, e
// vírgula ou parêntese quebrariam a lista de condições.
const aspas = (v) => `"${String(v).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;

/**
 * Em que papel o animal aparece num cruzamento, como filtro de `vw_cruzamentos`.
 *
 * O touro casa pelo NOME, porque é só o nome que o embrião guarda. A doadora,
 * pelo id. A receptora, pelo id e — só na transferência que não o tem — pelo
 * brinco digitado na TE; sem essa restrição, toda receptora 0362 de outro
 * cliente viria junto.
 *
 * Animal sem categoria nem sexo entra como doadora e receptora: é fêmea por
 * eliminação no rebanho daqui, e uma condição a mais não acha o que não existe.
 */
export const filtroDeCruzamentos = (animal = {}) => {
  const touro = ehTouro(animal);
  const semPapel = !touro && !ehDoadora(animal) && !ehReceptora(animal);
  const condicoes = [];
  if (touro && chaveDoNome(animal.identifier)) {
    condicoes.push(`touro_chave.eq.${aspas(chaveDoNome(animal.identifier))}`);
  }
  if (ehDoadora(animal) || semPapel) condicoes.push(`donor_id.eq.${animal.id}`);
  if (ehReceptora(animal) || semPapel) {
    condicoes.push(`recipient_id.eq.${animal.id}`);
    if (chaveDoNome(animal.identifier)) {
      condicoes.push(`and(recipient_id.is.null,receptora_chave.eq.${aspas(chaveDoNome(animal.identifier))})`);
    }
  }
  return condicoes.join(',');
};

/**
 * Receptora nasce mestiça.
 *
 * Receptora não tem raça que interesse a ninguém, mas a raça é obrigatória no
 * cadastro — e o técnico em campo preenchia o que dava: "S/", "S/IND", "não
 * identificada", "n/id". Mais de 300 receptoras ficaram assim, ao lado de
 * mil e tantas "Mestiço". O padrão vem preenchido e continua editável.
 *
 * `Ambos` fica de fora: ela também é doadora, e doadora tem raça.
 */
export const racaPadrao = (categoria) => (categoria === 'Receptora' ? 'Mestiço' : '');
